import type { CblurbackdropProps } from '@/components/ui/Cblurbackdrop/Cblurbackdrop';
import { modalLayerZIndices } from './modal-layer-z-indices';
import { findTopBackdropIndex } from './modal-stack-selectors';
import type { ModalBackdropConfig, ModalEntry } from './modal-stack-types';

type ModalBackdropHostProps = Pick<
  CblurbackdropProps,
  | 'animationDuration'
  | 'blur'
  | 'opacity'
  | 'style'
  | 'topOffset'
  | 'useGradient'
  | 'visible'
  | 'zIndexOverride'
>;

const DEFAULT_BACKDROP_OPACITY = 0.7;
const DEFAULT_BACKDROP_BLUR = 12;

export function getModalBackdropHostProps(
  stack: ModalEntry[],
): ModalBackdropHostProps {
  const selection = selectTopBackdrop(stack);

  return {
    visible: selection.backdrop != null,
    opacity: selectBackdropOpacity(selection.backdrop),
    blur: selectBackdropBlur(selection.backdrop),
    useGradient: selectBackdropUsesGradient(selection.backdrop),
    topOffset: selection.backdrop?.topOffset,
    animationDuration: selection.backdrop?.animationDuration,
    zIndexOverride: selection.zIndexOverride,
    style: selectBackdropStyle(selection.backdrop),
  };
}

function selectTopBackdrop(stack: ModalEntry[]): {
  backdrop: ModalBackdropConfig | null;
  zIndexOverride: number;
} {
  const topIndex = findTopBackdropIndex(stack);
  const topEntry = topIndex >= 0 ? stack[topIndex] : null;
  return {
    backdrop: topEntry?.backdrop ?? null,
    zIndexOverride: modalLayerZIndices(topIndex).backdrop,
  };
}

function selectBackdropOpacity(backdrop: ModalBackdropConfig | null): number {
  return backdrop?.opacity ?? DEFAULT_BACKDROP_OPACITY;
}

function selectBackdropBlur(backdrop: ModalBackdropConfig | null): number {
  return backdrop?.blur ?? DEFAULT_BACKDROP_BLUR;
}

function selectBackdropUsesGradient(
  backdrop: ModalBackdropConfig | null,
): boolean {
  return backdrop?.useGradient ?? true;
}

function selectBackdropStyle(
  backdrop: ModalBackdropConfig | null,
): NonNullable<ModalBackdropHostProps['style']> {
  return { pointerEvents: 'none', ...(backdrop?.style ?? {}) };
}
